import type { GoogleDrivePickedFile } from "@shared/unified-tiptap/extensions/link/link-upload"
import ENV from "cc-frontend/config/environment"
import type GooglePickerAuth from "cc-frontend/services/google-picker-auth"
import type Notifier from "cc-frontend/services/notifier"
import { useCallback } from "react"
import { useEmberService } from "react-app/providers/ember-service-context"

// The Google API is loaded at runtime by the google-picker-auth service.
declare const google: any

/** Common config for every Drive DocsView we offer (list mode, folders selectable). */
function docsView(): any {
  return new google.picker.DocsView(google.picker.ViewId.DOCS)
    .setSelectFolderEnabled(true)
    .setMode(google.picker.DocsViewMode.LIST)
}

/**
 * Open the Google Drive picker and resolve with the files the user chose (empty if they
 * cancelled).
 */
async function openGoogleDrivePicker(
  googlePickerAuth: GooglePickerAuth,
): Promise<GoogleDrivePickedFile[]> {
  const accessToken = await googlePickerAuth.requestAccessToken()

  return new Promise<GoogleDrivePickedFile[]>((resolve) => {
    const myFilesView = docsView().setOwnedByMe(true).setLabel("My Files")
    const myStarredFilesView = docsView()
      .setOwnedByMe(true)
      .setStarred(true)
      .setLabel("My Starred Files")
    const teamDriveView = new google.picker.DocsView(google.picker.ViewId.DOCS)
      .setEnableDrives(true)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true)
      .setLabel("Shared Files")
    const folderView = docsView().setIncludeFolders(true).setLabel("Folders")

    const picker = new google.picker.PickerBuilder()
      .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
      .enableFeature(google.picker.Feature.SUPPORT_TEAM_DRIVES)
      .addView(myFilesView)
      .addView(myStarredFilesView)
      .addView(teamDriveView)
      .addView(folderView)
      .addView(new google.picker.DocsUploadView())
      .addView(google.picker.ViewId.RECENTLY_PICKED)
      .setAppId(171418864917)
      .setDeveloperKey(ENV.GOOGLE_API_CONSOLE_KEY)
      .setOAuthToken(accessToken)
      .setCallback((data: any) => {
        if (data.action === google.picker.Action.CANCEL) {
          resolve([])
          return
        }
        if (data.action !== google.picker.Action.PICKED || data.docs === undefined) return

        resolve(
          data.docs.map(
            (doc: any): GoogleDrivePickedFile => ({
              title: doc.name,
              url: doc.url,
              icon: doc.iconUrl ?? null,
              type: doc.type === "image" ? "image" : "file",
            }),
          ),
        )
      })
      .build()

    picker.setVisible(true)
  })
}

/**
 * Returns a callback that opens the Google Drive picker for the Add menu, resolving with
 * the picked files (empty if the picker didn't load, the user cancelled, or auth failed).
 *
 * Use linkFromGooglePick to turn the picked files into Link attributes.
 */
export function useAddFromGoogleDrive(): () => Promise<GoogleDrivePickedFile[]> {
  const googlePickerAuth = useEmberService<GooglePickerAuth>("google-picker-auth")
  const notifier = useEmberService<Notifier>("notifier")

  return useCallback(async (): Promise<GoogleDrivePickedFile[]> => {
    if (typeof (window as unknown as { gapi?: unknown }).gapi === "undefined") {
      notifier.warning("Google didn't load their file picker correctly. Try refreshing the page.")
      return []
    }
    try {
      return await openGoogleDrivePicker(googlePickerAuth)
    } catch (error) {
      console.error("Google Drive picker failed", error)
      notifier.danger("Google Drive authentication wasn't successful.")
      return []
    }
  }, [googlePickerAuth, notifier])
}
